import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Tag, Text } from "@ttc3k/trekker";
import { Xmark } from "iconoir-react";
import { Flex, styled } from "styled-system/jsx";
import type { EventSessionOption } from "shared/utils";
import { EventDaySelector, formatDayLabel } from "./EventDaySelector";
import { EventTimeSlotSelector } from "./EventTimeSlotSelector";

type SessionAvailability = {
  isUnlimited?: boolean | null;
  remainingCapacity?: number | null;
};

type EventFlexSessionPickerProps = {
  requiredCount: number;
  value: string[];
  onChange: (next: string[]) => void;
  days: string[];
  sessionsByDate: Record<string, EventSessionOption[]>;
  hasTimeSlots: boolean;
  isDaySoldOut: (dateISO: string) => boolean;
  isToday?: (dateISO: string) => boolean;
  todayLabel?: string;
  availabilityBySessionId: Record<string, SessionAvailability | undefined>;
  language: string;
};

export const EventFlexSessionPicker = ({
  requiredCount,
  value,
  onChange,
  days,
  sessionsByDate,
  hasTimeSlots,
  isDaySoldOut,
  isToday,
  todayLabel,
  availabilityBySessionId,
  language,
}: EventFlexSessionPickerProps) => {
  const { t } = useTranslation("booker", { keyPrefix: "EVENT_TIER_SELECTOR" });
  const [activeDay, setActiveDay] = useState<string>(days[0] ?? "");

  const sessionById = useMemo(() => {
    const map: Record<string, EventSessionOption> = {};
    for (const sessions of Object.values(sessionsByDate)) {
      for (const session of sessions) map[session.sessionId] = session;
    }
    return map;
  }, [sessionsByDate]);

  // When the event offers only one session, select it automatically.
  const allSessionIds = useMemo(() => Object.keys(sessionById), [sessionById]);
  useEffect(() => {
    if (allSessionIds.length === 1 && value.length === 0) {
      onChange([allSessionIds[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSessionIds.join(","), value.length]);

  const chipLabel = (sessionId: string): string => {
    const session = sessionById[sessionId];
    if (!session) return sessionId;
    const dayLabel = formatDayLabel(session.dateISO, language);
    return session.timeLabel ? `${dayLabel} • ${session.timeLabel}` : dayLabel;
  };

  return (
    <Flex flexDirection="column" gap="150">
      <Text color="text.dark" visual="smallMedium">
        {t("PICK_N_PROMPT", { count: requiredCount, selected: value.length })}
      </Text>

      {hasTimeSlots ? (
        <Flex flexDirection="column" gap="150">
          <EventDaySelector
            days={days}
            value={activeDay ? [activeDay] : []}
            onChange={next => setActiveDay(next[0] ?? "")}
            isDaySoldOut={isDaySoldOut}
            isToday={isToday}
            todayLabel={todayLabel}
            language={language}
            placeholder={t("DATE_PLACEHOLDER")}
          />
          {activeDay && (
            <EventTimeSlotSelector
              slots={sessionsByDate[activeDay] ?? []}
              value={value}
              onChange={onChange}
              multiple
              maxCount={requiredCount}
              availabilityBySessionId={availabilityBySessionId}
            />
          )}
          {value.length > 0 && (
            <Flex gap="100" flexWrap="wrap">
              {value.map(sessionId => (
                <Tag key={sessionId} visual="highlight" mode="light">
                  <Flex alignItems="center" gap="50">
                    {chipLabel(sessionId)}
                    <styled.button
                      type="button"
                      cursor="pointer"
                      display="inline-flex"
                      onClick={() =>
                        onChange(value.filter(id => id !== sessionId))
                      }
                    >
                      <Icon Element={Xmark} width="14px" height="14px" />
                    </styled.button>
                  </Flex>
                </Tag>
              ))}
            </Flex>
          )}
        </Flex>
      ) : (
        <EventDaySelector
          days={days}
          value={value}
          onChange={onChange}
          multiple
          maxCount={requiredCount}
          isDaySoldOut={isDaySoldOut}
          isToday={isToday}
          todayLabel={todayLabel}
          language={language}
          showChips
          placeholder={t("DATES_PLACEHOLDER")}
        />
      )}
    </Flex>
  );
};
